import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { msalInstance, portalScopes } from '@/lib/auth'
import { config } from '@/lib/config'

export class ApiError extends Error {
  requestId?: string
  constructor(public status: number, message: string, requestId?: string) {
    super(message)
    this.name = 'ApiError'
    this.requestId = requestId
  }
}

async function getToken(): Promise<string> {
  const account = msalInstance.getAllAccounts()[0]
  if (!account) throw new Error('No active session')
  try {
    const result = await msalInstance.acquireTokenSilent({ ...portalScopes, account })
    return result.accessToken
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ ...portalScopes, account })
    }
    throw e
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getToken()
  const controller = new AbortController()
  // SQL serverless can take 20-60s to resume from auto-pause
  const timeoutId = setTimeout(() => controller.abort(), 60_000)
  try {
    const res = await fetch(`${config.apiUrl}${path}`, {
      ...init,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...init?.headers,
      },
    })

    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new ApiError(res.status, body.message ?? body.error ?? `HTTP ${res.status}`, body.requestId)
    }

    return res.json() as Promise<T>
  } finally {
    clearTimeout(timeoutId)
  }
}

export const api = {
  get:    <T>(path: string)                => request<T>(path),
  post:   <T>(path: string, body: unknown) => request<T>(path, { method: 'POST',   body: JSON.stringify(body) }),
  put:    <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT',    body: JSON.stringify(body) }),
  patch:  <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH',  body: JSON.stringify(body) }),
  delete: <T>(path: string)               => request<T>(path, { method: 'DELETE' }),
}
